import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'

import Button from '../../../common/button'
import { ModalButton } from '../../../common/modal'
import EmptyState from '../../../common/empty-state/empty-state'
import IconPlus from '../../../common/icons/icon-plus'
import IconSaveDisk from '../../../common/icons/icon-save-disk'
import ErrorModal from '../../../common/error-modal'
import { PanelSection, PanelSectionContent, PanelSectionHeader } from '../../../common/panels/panel-section'

import connectSave from '../../../visualizer/panels/buttons/connect-save'
import SaveDialog from './save-dialog'
import { SavedSceneList } from './savedSceneList'

import { AppDispatch } from '../../../../stores/ducks'
import * as fromProjects from '../../../../stores/ducks/projects'
import { getCurrentId } from '../../../../stores/ducks/projects/selectors'
import * as fromScenes from '../../../../stores/ducks/scenes'
import { getScenesList } from '../../../../stores/ducks/scenes/selectors'

type ConnectSaveProps = {
  disableKeyBoardBindings: () => void
  enableKeyBoardBindings: () => void
  notificationMessage: string
}

type Props = {
  scenesList: ReturnType<typeof getScenesList>
  projectId: string | null
}

const SaveModalButton = (props: ConnectSaveProps & { button: (openModal: () => void) => React.ReactNode }) => {
  const dispatch = useDispatch()

  const handleSaveDialogSave = (closeModal: () => void) => () => {
    dispatch(fromProjects.setNotificationMessage(props.notificationMessage))
    closeModal()
  }

  return (
    <ModalButton
      small
      modalContent={(closeModal: () => void) => (
        <SaveDialog
          onSave={handleSaveDialogSave(closeModal)}
          onClose={closeModal}
        />
      )}
      button={props.button}
      onModalClose={() => {
        props.enableKeyBoardBindings()
      }}
    />
  )
}

const EmptyStateSaveScene = connectSave('render')((props: ConnectSaveProps) => {
  return (
    <SaveModalButton
      {...props}
      notificationMessage="Changes saved"
      button={(openModal: () => void) => (
        <EmptyState
          className='mt4'
          icon={<IconSaveDisk size={80} />}
          title='Save the progress'
          desc='Use Save button to store the current state of your work so you can continue later, without creating new images.'
          docLink='saved.md'
          linkText='Learn more'
          onAction={() => {
            props.disableKeyBoardBindings()
            openModal()
          }}
          actionTitle='Save current'
        />
      )}
    />
  )
})

const AddCurrentButton = connectSave('render')((props: ConnectSaveProps) => {
  return (
    <SaveModalButton
      {...props}
      notificationMessage="New save added"
      button={(openModal: () => void) => (
        <Button
          data-testid='saved-panel-add-current'
          pad={1}
          onClick={() => {
            props.disableKeyBoardBindings()
            openModal()
          }}
          icon={<IconPlus width='1.2em' height='1.2em' />}
        >
          Add Current
        </Button>
      )}
    />
  )
})

const SavedScenesPanel = (props: Props) => {
  const dispatch = useDispatch<AppDispatch>()
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)

  useEffect(() => {
    const { projectId } = props
    if (projectId) {
      (async () => {
        try {
          await dispatch(fromScenes.fetchScenesByProject(projectId))
        } catch {
          setIsErrorModalOpen(true)
        }
      })()
    }
  }, [props.projectId])

  const hasScenes = props.scenesList?.length > 0

  return (
    <>
      <ErrorModal
        isOpen={isErrorModalOpen}
        title='Load failed'
        description="Saves couldn't be loaded. Please try again."
        handleClose={() => setIsErrorModalOpen(false)}
      />
      <PanelSection className='overflow-auto'>
        <PanelSectionHeader title='Saved' />
        {!hasScenes && (
          <EmptyStateSaveScene />
        )}
        {hasScenes && (
          <PanelSectionContent>
            <AddCurrentButton/>
            <SavedSceneList />
          </PanelSectionContent>
        )}
      </PanelSection>
    </>
  )
}

const mapStateToProps = (state: any) => ({
  scenesList: getScenesList(state),
  projectId: getCurrentId(state),
})

export default connect(
  mapStateToProps
)(SavedScenesPanel)
